import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useShallow } from 'zustand/react/shallow';
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
} from '@roger/ui-components';
import {
  AlertTriangle,
  Edit,
  Loader2,
  Plus,
  RefreshCw,
  RotateCcw,
  Trash2,
} from 'lucide-react';
import {
  createFormTemplateAdminFormTemplatesPost as createFormTemplate,
  deleteFormTemplateAdminFormTemplatesTemplateIdDelete as retireFormTemplate,
  getRogerDocFormTypeCatalogRogerdocFormTypeCatalogGet as listFormTypeCatalog,
  getVersionedQuestionSchemaRogerdocV2QuestionSchemaPost as getVersionedQuestionSchema,
  listAllowedFormTemplateQuestionsAdminFormTemplatesAllowedQuestionsGet as listAllowedQuestions,
  listFormTemplatesAdminFormTemplatesGet as listFormTemplates,
  updateFormTemplateAdminFormTemplatesTemplateIdPut as updateFormTemplate,
} from '../../services/api/sdk.gen';
import type {
  FormTemplateResponse,
  RogerDocFormTypeCatalogEntry,
  VersionedQuestionSchema,
} from '../../services/api/types.gen';
import type { FormTreeQuestion } from '../../components/oasis/types';
import {
  collectTextFieldOptions,
  type TextFieldOption,
} from './collectTextFieldOptions';
import { FormFieldCombobox } from './FormFieldCombobox';
import { useStore } from '../../store';
import { useAdminSectionAccess } from '../../hooks/useAdminSectionAccess';
import { useClientPagedList } from '../../hooks/useClientPagedList';
import { useStaleSafeLoader } from '../../hooks/useStaleSafeLoader';
import TablePaginationFooter from '../../components/TablePaginationFooter';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import ConfirmRetireDialog from './ConfirmRetireDialog';

/** Mirrors the backend caps so an over-long template fails before the request. */
const NAME_MAX = 200;
const TEMPLATE_TEXT_MAX = 50000;

const templateFormSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Name is required')
    .max(NAME_MAX, `Name must be ${NAME_MAX} characters or fewer`),
  // Deliberately not trimmed, unlike the name above. Exact-format wording is
  // loaded into the field verbatim, so an admin's indentation or trailing blank
  // line is part of the content rather than slack to tidy away, and the backend
  // does not strip it either. Blank-only is still refused, just refused without
  // rewriting what they typed.
  templateText: z
    .string()
    .min(1, 'Template text is required')
    .max(
      TEMPLATE_TEXT_MAX,
      `Template text must be ${TEMPLATE_TEXT_MAX} characters or fewer`
    )
    .refine(value => value.trim().length > 0, {
      message: 'Template text is required',
    }),
  kind: z.enum(['exact_format', 'general_guidance']),
});

type TemplateFormData = z.infer<typeof templateFormSchema>;

const TEMPLATE_FORM_DEFAULTS: TemplateFormData = {
  name: '',
  templateText: '',
  kind: 'exact_format',
};

const PAGE_SIZE_OPTIONS = [10, 25, 50] as const;

// Matches the question id even though no row displays it. An id is what a
// spec or a support thread names, so somebody who arrives holding one has to
// be able to find the templates on it.
function matchesFormTemplate(
  template: FormTemplateResponse,
  normalizedSearch: string
): boolean {
  return (
    template.name.toLowerCase().includes(normalizedSearch) ||
    template.field_label.toLowerCase().includes(normalizedSearch) ||
    template.qid.toLowerCase().includes(normalizedSearch)
  );
}

/**
 * Admin screen for the standardized text an agency loads into visit-form boxes.
 *
 * The target field is chosen through a form picker, but what gets stored is the
 * question id alone. Roger builds its form schemas from one shared question
 * tree, so the same text question really is reused across many form types, and
 * a template keyed to the question follows it everywhere. Choosing a form is
 * how an admin *finds* the question they mean, not a scope on the result.
 *
 * Only questions the backend allows templates on are offered. The form catalog
 * holds around a thousand text questions and almost all of them are one-line
 * boxes, so the picker asks the server which ones this is for rather than
 * making the admin judge it a form at a time.
 *
 * The target is fixed once a template exists. The prose is written to answer a
 * specific question, so retargeting would quietly move goals wording onto a
 * narrative field; authoring a second template is the intended path, which is
 * why the picker only appears while creating.
 *
 * Retiring is not deleting. A template can be brought back, because the text is
 * prose somebody composed and nothing else holds a copy of it.
 */
const FormTemplatesSection: React.FC = () => {
  const { showSuccess, showError } = useStore(
    useShallow(state => ({
      showSuccess: state.showSuccess,
      showError: state.showError,
    }))
  );
  const { canEdit } = useAdminSectionAccess('form-templates');

  const [templates, setTemplates] = useState<FormTemplateResponse[]>([]);
  // Monotonic load generation; only the newest load may write to the table.
  const { isLoading, loadFailed, run: runLoad } = useStaleSafeLoader();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<FormTemplateResponse | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [pendingRetire, setPendingRetire] =
    useState<FormTemplateResponse | null>(null);

  // Target picker state, only meaningful while creating.
  const [formTypes, setFormTypes] = useState<RogerDocFormTypeCatalogEntry[]>(
    []
  );
  const [selectedFormType, setSelectedFormType] = useState<string>('');
  const [textFields, setTextFields] = useState<TextFieldOption[]>([]);
  // `section_id` to the heading the form itself uses, so the picker's groups
  // read the same way as the form's own section list.
  const [sectionLabels, setSectionLabels] = useState<Record<string, string>>(
    {}
  );
  const [fieldsLoading, setFieldsLoading] = useState(false);
  const [fieldsError, setFieldsError] = useState<string | null>(null);
  const [selectedQid, setSelectedQid] = useState<string>('');
  // Questions that are allowed to carry templates at all, or null until the
  // list has been answered for. Held as a set because both the picker and the
  // table ask it per row.
  const [allowedQids, setAllowedQids] = useState<Set<string> | null>(null);
  const [allowedQidsFailed, setAllowedQidsFailed] = useState(false);
  // Monotonic guard for the per-form schema fetch: switching forms quickly must
  // not let a slow earlier response repopulate the field list.
  const schemaRequestIdRef = useRef(0);

  const {
    search,
    setSearch,
    pageSize,
    setPageSize,
    pageIndex: safePageIndex,
    setPageIndex,
    pageCount,
    filtered: filteredTemplates,
    visible: visibleTemplates,
    canPreviousPage,
    canNextPage,
  } = useClientPagedList({
    items: templates,
    matchesSearch: matchesFormTemplate,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
  });

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<TemplateFormData>({
    resolver: zodResolver(templateFormSchema),
    defaultValues: TEMPLATE_FORM_DEFAULTS,
    mode: 'onSubmit',
  });
  const kind = watch('kind');

  const loadTemplates = useCallback(async () => {
    await runLoad(
      async () => {
        const result = await listFormTemplates({ throwOnError: true });
        return result.data ?? [];
      },
      {
        trackLoadFailed: true,
        onSuccess: setTemplates,
        onError: error => {
          console.error('Failed to load form templates', error);
          showError('Failed to load text templates');
        },
      }
    );
  }, [runLoad, showError]);

  useEffect(() => {
    void loadTemplates();
  }, [loadTemplates]);

  // The form catalog is only needed to author a template, so it is fetched when
  // the section loads for somebody who can write and never for a viewer.
  useEffect(() => {
    if (!canEdit) return;
    let cancelled = false;
    listFormTypeCatalog()
      .then(response => {
        if (cancelled) return;
        setFormTypes(response.data ?? []);
      })
      .catch(error => {
        if (cancelled) return;
        console.error('Failed to load form type catalog', error);
      });
    return () => {
      cancelled = true;
    };
  }, [canEdit]);

  // Fetched for a viewer too, unlike the form catalog above: the table marks a
  // template whose question is no longer offered, and a viewer reads that same
  // table.
  useEffect(() => {
    let cancelled = false;
    listAllowedQuestions()
      .then(response => {
        if (cancelled) return;
        if (response.error || !response.data) {
          throw response.error ?? new Error('No allowed questions returned');
        }
        setAllowedQids(new Set(response.data));
        setAllowedQidsFailed(false);
      })
      .catch(error => {
        if (cancelled) return;
        console.error('Failed to load templatable questions', error);
        setAllowedQidsFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const loadTextFieldsForForm = useCallback(
    async (formType: string) => {
      const entry = formTypes.find(
        candidate => candidate.form_type === formType
      );
      if (!entry) return;

      schemaRequestIdRef.current += 1;
      const requestId = schemaRequestIdRef.current;
      const isCurrent = () => schemaRequestIdRef.current === requestId;

      setFieldsLoading(true);
      setFieldsError(null);
      setTextFields([]);
      setSectionLabels({});
      setSelectedQid('');
      try {
        const response = await getVersionedQuestionSchema({
          query: { visit_type: entry.canonical_visit_type_id },
          throwOnError: true,
        });
        if (!isCurrent()) return;
        const payload = response.data as VersionedQuestionSchema | undefined;
        const schemaData = (payload?.schema_data ?? {}) as Record<
          string,
          FormTreeQuestion[]
        >;

        const order = payload?.version?.section_order ?? [];
        setSectionLabels(
          Object.fromEntries(
            order
              .filter(item => item?.section_id && item.display_name)
              .map(item => [item.section_id, item.display_name])
          )
        );
        setTextFields(
          collectTextFieldOptions(
            schemaData,
            order.map(item => item.section_id)
          )
        );
      } catch (error) {
        console.error('Failed to load form fields', error);
        if (isCurrent()) {
          // Named rather than generic: the usual cause is the schema service
          // being unavailable, and "try again" is the right next step.
          setFieldsError(
            'Could not load this form. The form service may be unavailable.'
          );
        }
      } finally {
        if (isCurrent()) setFieldsLoading(false);
      }
    },
    [formTypes]
  );

  /**
   * The form's text questions that are allowed to carry a template.
   *
   * Empty while the allowed list is still loading, and empty if it failed. The
   * create route refuses a question off the list anyway, so offering the whole
   * form here would only produce a refusal after the admin had written the
   * wording; a failed load says so under the picker instead.
   */
  const templatableFields = useMemo(() => {
    if (!allowedQids) return [];
    return textFields.filter(option => allowedQids.has(option.qid));
  }, [textFields, allowedQids]);

  const openCreateModal = useCallback(() => {
    setEditing(null);
    setSubmitError(null);
    setSelectedFormType('');
    setTextFields([]);
    setSectionLabels({});
    setSelectedQid('');
    setFieldsError(null);
    reset(TEMPLATE_FORM_DEFAULTS);
    setIsModalOpen(true);
  }, [reset]);

  const openEditModal = useCallback(
    (template: FormTemplateResponse) => {
      setEditing(template);
      setSubmitError(null);
      reset({
        name: template.name,
        templateText: template.template_text,
        kind: template.kind,
      });
      setIsModalOpen(true);
    },
    [reset]
  );

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setEditing(null);
    setSubmitError(null);
  }, []);

  const onSubmit = useCallback(
    async (data: TemplateFormData) => {
      setSubmitError(null);
      try {
        if (editing) {
          await updateFormTemplate({
            path: { template_id: editing.id },
            body: {
              name: data.name,
              template_text: data.templateText,
              kind: data.kind,
            },
            throwOnError: true,
          });
          showSuccess('Template updated');
        } else {
          const field = templatableFields.find(
            option => option.qid === selectedQid
          );
          if (!field) {
            setSubmitError('Choose the form field this template is for.');
            return;
          }
          await createFormTemplate({
            body: {
              name: data.name,
              qid: field.qid,
              field_label: field.label,
              template_text: data.templateText,
              kind: data.kind,
            },
            throwOnError: true,
          });
          showSuccess('Template added');
        }
        closeModal();
        await loadTemplates();
      } catch (error) {
        console.error('Failed to save form template', error);
        // The duplicate-name refusal names the problem, so it is worth showing
        // in the form rather than replacing with a generic retry.
        setSubmitError(
          getBusinessErrorMessage(error) ??
            'Failed to save this template. Please try again.'
        );
      }
    },
    [
      editing,
      templatableFields,
      selectedQid,
      closeModal,
      loadTemplates,
      showSuccess,
    ]
  );

  const confirmRetire = useCallback(async () => {
    if (!pendingRetire) return;
    setBusyId(pendingRetire.id);
    try {
      await retireFormTemplate({
        path: { template_id: pendingRetire.id },
        throwOnError: true,
      });
      showSuccess(`${pendingRetire.name} retired`);
      setPendingRetire(null);
      await loadTemplates();
    } catch (error) {
      console.error('Failed to retire form template', error);
      showError(
        getBusinessErrorMessage(error) ??
          'Failed to retire this template. Please try again.'
      );
    } finally {
      setBusyId(null);
    }
  }, [pendingRetire, loadTemplates, showSuccess, showError]);

  const reactivate = useCallback(
    async (template: FormTemplateResponse) => {
      setBusyId(template.id);
      try {
        await updateFormTemplate({
          path: { template_id: template.id },
          body: { is_active: true },
          throwOnError: true,
        });
        showSuccess(`${template.name} restored`);
        await loadTemplates();
      } catch (error) {
        console.error('Failed to restore form template', error);
        showError(
          getBusinessErrorMessage(error) ??
            'Failed to restore this template. Please try again.'
        );
      } finally {
        setBusyId(null);
      }
    },
    [loadTemplates, showSuccess, showError]
  );

  return (
    <div className='space-y-4'>
      <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
        <div>
          <h3 className='text-base font-semibold'>Text Templates</h3>
          <p className='text-sm text-muted-foreground'>
            Standard wording clinicians can load into a visit form text box
            instead of typing it out. A template applies wherever its field
            appears.
          </p>
        </div>
        <div className='flex flex-wrap items-center gap-2'>
          <Button
            variant='outline'
            size='sm'
            onClick={() => void loadTemplates()}
            disabled={isLoading}
          >
            <RefreshCw className='mr-1 h-4 w-4' />
            Refresh
          </Button>
          {canEdit && (
            <Button size='sm' onClick={openCreateModal}>
              <Plus className='mr-1 h-4 w-4' />
              Add Template
            </Button>
          )}
        </div>
      </div>

      {loadFailed && (
        <Alert variant='destructive'>
          <AlertTriangle className='h-4 w-4' />
          <AlertDescription>
            Could not load text templates. Use Refresh to try again.
          </AlertDescription>
        </Alert>
      )}

      <Input
        placeholder='Search by name or field'
        value={search}
        onChange={event => setSearch(event.target.value)}
        inputSize='sm'
        className='w-full md:w-72'
      />

      <div className='overflow-x-auto'>
        <Table className='min-w-[720px]'>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Form field</TableHead>
              <TableHead>Use</TableHead>
              <TableHead>Status</TableHead>
              {canEdit && <TableHead className='w-28'>Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && templates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={canEdit ? 5 : 4}>
                  <span className='flex items-center gap-2 text-sm text-muted-foreground'>
                    <Loader2 className='h-4 w-4 animate-spin' />
                    Loading templates...
                  </span>
                </TableCell>
              </TableRow>
            ) : visibleTemplates.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={canEdit ? 5 : 4}
                  className='text-sm text-muted-foreground'
                >
                  No text templates yet.
                </TableCell>
              </TableRow>
            ) : (
              visibleTemplates.map(template => (
                <TableRow key={template.id}>
                  <TableCell className='font-medium'>{template.name}</TableCell>
                  <TableCell>
                    {/* Two questions can carry the same wording on different
                        form families, so a saved prompt is not always unique
                        across the library. The id settles it for anybody who
                        needs to, without putting a hash in front of everybody
                        who does not. */}
                    <span className='block text-sm' title={template.qid}>
                      {template.field_label}
                    </span>
                    {/* A template can outlive its field being allowed to have
                        one. It keeps its wording and comes back if the field is
                        allowed again, but it is not offered on the form
                        meanwhile, and an active row that does nothing would
                        otherwise look like a bug. */}
                    {allowedQids &&
                      template.is_active &&
                      !allowedQids.has(template.qid) && (
                        <span className='block text-xs text-muted-foreground'>
                          This field no longer accepts templates, so it is not
                          offered on the form.
                        </span>
                      )}
                  </TableCell>
                  <TableCell>
                    {template.kind === 'exact_format' ? (
                      <Badge variant='outline'>Exact format</Badge>
                    ) : (
                      <Badge variant='secondary'>Guidance</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {template.is_active ? (
                      <Badge variant='outline'>Active</Badge>
                    ) : (
                      <Badge variant='secondary'>Retired</Badge>
                    )}
                  </TableCell>
                  {canEdit && (
                    <TableCell>
                      <div className='flex items-center gap-1'>
                        <Button
                          variant='ghost'
                          size='sm'
                          aria-label={`Edit ${template.name}`}
                          onClick={() => openEditModal(template)}
                          disabled={busyId === template.id}
                        >
                          <Edit className='h-4 w-4' />
                        </Button>
                        {template.is_active ? (
                          <Button
                            variant='ghost'
                            size='sm'
                            aria-label={`Retire ${template.name}`}
                            onClick={() => setPendingRetire(template)}
                            disabled={busyId === template.id}
                          >
                            <Trash2 className='h-4 w-4' />
                          </Button>
                        ) : (
                          <Button
                            variant='ghost'
                            size='sm'
                            aria-label={`Restore ${template.name}`}
                            onClick={() => void reactivate(template)}
                            disabled={busyId === template.id}
                          >
                            <RotateCcw className='h-4 w-4' />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <TablePaginationFooter
        pageIndex={safePageIndex}
        pageCount={pageCount}
        totalCount={filteredTemplates.length}
        pageSize={pageSize}
        pageSizeOptions={[...PAGE_SIZE_OPTIONS]}
        canPreviousPage={canPreviousPage}
        canNextPage={canNextPage}
        onPageSizeChange={setPageSize}
        onPreviousPage={() => setPageIndex(index => Math.max(0, index - 1))}
        onNextPage={() =>
          setPageIndex(index => Math.min(pageCount - 1, index + 1))
        }
      />

      <Dialog open={isModalOpen} onOpenChange={open => !open && closeModal()}>
        <DialogContent className='max-w-2xl'>
          <DialogHeader>
            <DialogTitle>
              {editing ? 'Edit Template' : 'Add Template'}
            </DialogTitle>
            <DialogDescription>
              {editing
                ? 'The form field a template is written for cannot be changed. Add a separate template for a different field.'
                : 'Pick the form field this wording is for, then write the text clinicians should start from.'}
            </DialogDescription>
          </DialogHeader>

          <form
            onSubmit={handleSubmit(onSubmit)}
            className='space-y-4'
            noValidate
          >
            {!editing && (
              <>
                <div className='space-y-1.5'>
                  <Label>Form</Label>
                  <Select
                    value={selectedFormType}
                    onValueChange={value => {
                      setSelectedFormType(value);
                      void loadTextFieldsForForm(value);
                    }}
                  >
                    <SelectTrigger selectSize='sm'>
                      <SelectValue placeholder='Choose a form' />
                    </SelectTrigger>
                    <SelectContent>
                      {formTypes.map(entry => (
                        <SelectItem
                          key={entry.form_type}
                          value={entry.form_type}
                        >
                          {entry.display_label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className='text-xs text-muted-foreground'>
                    Only used to find the field. The template will apply on
                    every form that asks the same question.
                  </p>
                </div>

                <div className='space-y-1.5'>
                  <Label>Form field</Label>
                  {/* Searchable rather than a plain dropdown: a form carries
                      dozens of text questions and their prompts are full
                      sentences, so scrolling for a half-remembered one is the
                      slow part of authoring a template. */}
                  <FormFieldCombobox
                    options={templatableFields}
                    sectionLabels={sectionLabels}
                    value={selectedQid}
                    onValueChange={setSelectedQid}
                    disabled={
                      !selectedFormType ||
                      fieldsLoading ||
                      templatableFields.length === 0
                    }
                    placeholder={
                      fieldsLoading
                        ? 'Loading fields...'
                        : !selectedFormType
                          ? 'Choose a form first'
                          : // Still waiting on the allowed list is not the same
                            // as the form having nothing to offer, and saying
                            // so would send the admin off to another form.
                            !allowedQids && !allowedQidsFailed
                            ? 'Loading fields...'
                            : templatableFields.length === 0
                              ? 'No fields on this form accept templates'
                              : 'Choose a field'
                    }
                  />
                  {/* Only the fields templates are meant for are offered, so an
                      admin browsing a form does not have to judge which of its
                      hundred text boxes is worth standard wording. */}
                  <p className='text-xs text-muted-foreground'>
                    Only fields that accept templates are listed.
                  </p>
                  {fieldsError && (
                    <p className='text-xs text-destructive'>{fieldsError}</p>
                  )}
                  {allowedQidsFailed && (
                    <p className='text-xs text-destructive'>
                      Could not load which fields accept templates. Reopen this
                      section to try again.
                    </p>
                  )}
                </div>
              </>
            )}

            {editing && (
              <div className='space-y-1.5'>
                <Label>Form field</Label>
                <p className='text-sm' title={editing.qid}>
                  {editing.field_label}
                </p>
              </div>
            )}

            <div className='space-y-1.5'>
              <Label htmlFor='form-template-name'>Name</Label>
              <Input
                id='form-template-name'
                inputSize='sm'
                placeholder='What clinicians will see in the picker'
                {...register('name')}
              />
              {errors.name && (
                <p className='text-xs text-destructive'>
                  {errors.name.message}
                </p>
              )}
            </div>

            <div className='space-y-1.5'>
              <Label htmlFor='form-template-text'>Template text</Label>
              <Textarea
                id='form-template-text'
                textareaSize='default'
                className='min-h-[160px]'
                placeholder='Wording to load into the field. Leave blanks for the clinician to fill in.'
                {...register('templateText')}
              />
              {errors.templateText && (
                <p className='text-xs text-destructive'>
                  {errors.templateText.message}
                </p>
              )}
            </div>

            <div className='space-y-1.5'>
              <Label>How it is used</Label>
              <Select
                value={kind}
                onValueChange={value =>
                  setValue('kind', value as TemplateFormData['kind'])
                }
              >
                <SelectTrigger selectSize='sm'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='exact_format'>
                    Exact format, load into the field
                  </SelectItem>
                  <SelectItem value='general_guidance'>
                    General guidance, not offered in the form
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className='text-xs text-muted-foreground'>
                {kind === 'exact_format'
                  ? 'Clinicians can load this wording straight into the field.'
                  : 'Guidance describes how to write the answer rather than being the answer, so it is not offered in the form. Saved here ready for AI-assisted documentation.'}
              </p>
            </div>

            {submitError && (
              <Alert variant='destructive'>
                <AlertTriangle className='h-4 w-4' />
                <AlertDescription>{submitError}</AlertDescription>
              </Alert>
            )}

            <DialogFooter>
              <Button
                type='button'
                variant='outline'
                onClick={closeModal}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type='submit' disabled={isSubmitting}>
                {isSubmitting && (
                  <Loader2 className='mr-1 h-4 w-4 animate-spin' />
                )}
                {editing ? 'Save' : 'Add Template'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmRetireDialog
        open={pendingRetire !== null}
        onOpenChange={open => {
          if (!open) setPendingRetire(null);
        }}
        title='Retire this template?'
        description={
          <>
            {pendingRetire?.name} stops being offered on the form. The wording
            is kept and you can restore it later.
          </>
        }
        onConfirm={() => void confirmRetire()}
        confirming={busyId !== null}
        disableCancelWhileConfirming
      />
    </div>
  );
};

export default FormTemplatesSection;
